import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  CircleEllipsis,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Monitor,
  Smartphone,
  Code,
  FileCode2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  htmlTemplatesApi,
  type HtmlTemplateItem,
  type SourceMetadata,
} from '../../../services/htmlTemplatesApi';
import { useClientLevel } from '../../../hooks/useClientLevel';
import { CustomConfirm } from './CustomConfirm';
import { CustomSelect, type SelectOption } from './CustomSelect';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import HtmlEmailPreview from './templates/HtmlEmailPreview';

export const TemplatesTab: React.FC = () => {
  const navigate = useNavigate();
  const currentLevel = useClientLevel();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by') || '';
  const sortOrder = (searchParams.get('order') || searchParams.get('sort_order') || 'asc') as 'asc' | 'desc';

  const [templates, setTemplates] = useState<HtmlTemplateItem[]>([]);
  const [sources, setSources] = useState<SourceMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Search state with debouncing (API level)
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Actions portal menu state (matching RulesTab.tsx)
  const [activeActionMenuId, setActiveActionMenuId] = useState<number | string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Pagination state (matching RulesTab.tsx)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Quick Preview Modal state
  const [previewTemplate, setPreviewTemplate] = useState<HtmlTemplateItem | null>(null);
  const [previewTab, setPreviewTab] = useState<'open' | 'close'>('open');
  const [previewDeviceView, setPreviewDeviceView] = useState<'desktop' | 'mobile'>('desktop');
  const [previewShowRawHtml, setPreviewShowRawHtml] = useState<boolean>(false);

  // Delete Confirmation Modal state
  const [templateToDelete, setTemplateToDelete] = useState<HtmlTemplateItem | null>(null);

  // Create Template Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [newTemplateDesc, setNewTemplateDesc] = useState<string>('');
  const [newTemplateSource, setNewTemplateSource] = useState<string>('');

  // Sorting handler matching RulesTab.tsx
  const handleSort = (field: string) => {
    let nextOrder: 'asc' | 'desc' = 'asc';
    if (sortBy === field) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort_by', field);
        next.set('order', nextOrder);
        return next;
      },
      { replace: true }
    );
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-400 dark:text-gray-500 transition-opacity" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
    ) : (
      <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />
    );
  };

  // Debounce search query to trigger API search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const isClientLevel = Boolean(
    currentLevel &&
    currentLevel.name !== 'global' &&
    (currentLevel.id !== undefined || currentLevel.client_id !== undefined)
  );

  const currentClientId = isClientLevel
    ? Number(currentLevel?.id !== undefined ? currentLevel?.id : currentLevel?.client_id)
    : undefined;

  const buildQuery = (searchTerm: string) => {
    const queryObj: any = {};
    const s = searchTerm.trim();

    if (isClientLevel && currentClientId !== undefined) {
      // Client Level: Match templates belonging to this client OR global templates
      const scopeCondition = {
        $or: [
          { is_global: true },
          { client_id: currentClientId },
        ],
      };

      if (s) {
        const searchCondition = {
          $or: [
            { name: { $ilike: `%${s}%` } },
            { description: { $ilike: `%${s}%` } },
            { source: { $ilike: `%${s}%` } },
          ],
        };
        queryObj.$and = [scopeCondition, searchCondition];
      } else {
        queryObj.$or = scopeCondition.$or;
      }
    } else {
      // Global Level: ONLY show global templates
      queryObj.is_global = true;

      if (s) {
        queryObj.$or = [
          { name: { $ilike: `%${s}%` } },
          { description: { $ilike: `%${s}%` } },
          { source: { $ilike: `%${s}%` } },
        ];
      }
    }

    return Object.keys(queryObj).length > 0 ? JSON.stringify(queryObj) : undefined;
  };

  // Fetch sources and templates via API query
  const fetchData = async (searchTerm?: string) => {
    setLoading(true);
    try {
      const termToQuery = searchTerm !== undefined ? searchTerm : debouncedSearch;
      const queryStr = buildQuery(termToQuery);
      const [sourcesRes, templatesRes] = await Promise.all([
        htmlTemplatesApi.getSources().catch(() => ({ data: [] })),
        htmlTemplatesApi.getTemplates({
          query: queryStr,
          include_global: isClientLevel,
          sort_by: sortBy || undefined,
          sort_order: sortOrder || undefined,
          limit: 200,
        }),
      ]);

      setSources(sourcesRes.data || []);
      let items = templatesRes.data || [];

      // Strict client-side filter fallback guaranteeing multi-tenant visibility
      if (isClientLevel) {
        // Client level: Show templates belonging to this client OR global templates
        items = items.filter(
          (t: any) =>
            t.is_global === true ||
            (currentClientId !== undefined && Number(t.client_id) === currentClientId)
        );
      } else {
        // Global level: ONLY show global templates
        items = items.filter(
          (t: any) => t.is_global === true || (!t.client_id && t.is_global !== false)
        );
      }

      // Client-side sort fallback if API doesn't order directly
      if (sortBy) {
        items = [...items].sort((a: any, b: any) => {
          let valA = a[sortBy] ?? '';
          let valB = b[sortBy] ?? '';
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setTemplates(items);
    } catch (err: any) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load HTML templates from server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, sortBy, sortOrder, currentLevel]);

  useEffect(() => {
    const handleLevelChange = () => {
      setActiveActionMenuId(null);
      fetchData();
    };
    window.addEventListener('client-level-changed', handleLevelChange);
    return () => window.removeEventListener('client-level-changed', handleLevelChange);
  }, [debouncedSearch, sortBy, sortOrder, currentLevel]);

  // Close Action Menu on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        activeActionMenuId !== null &&
        !target.closest('.action-menu-portal') &&
        !target.closest('.action-menu-trigger')
      ) {
        setActiveActionMenuId(null);
      }
    };
    const handleScroll = () => {
      if (activeActionMenuId !== null) {
        setActiveActionMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeActionMenuId]);

  const handleActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, templateId: number | string) => {
    e.stopPropagation();
    if (activeActionMenuId === templateId) {
      setActiveActionMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 176;
      const menuHeight = 170;

      let left = rect.right - menuWidth;
      if (left < 10) left = 10;

      let top = rect.bottom + 4;
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }
      if (top < 10) top = 10;

      setActionMenuPosition({ top, left });
      setActiveActionMenuId(templateId);
    }
  };

  // Date formatting helper matching project standards (without relative time)
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      let normalizedStr = dateStr;
      if (!dateStr.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(dateStr)) {
        normalizedStr += 'Z';
      }
      const d = new Date(normalizedStr);
      if (isNaN(d.getTime())) return dateStr;
      return (
        d.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }) +
        ' ' +
        d.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    } catch {
      return dateStr;
    }
  };

  const sourceOptions: SelectOption[] = useMemo(() => {
    return sources.map((s) => ({
      label: s.label || s.source,
      value: s.source,
    }));
  }, [sources]);

  const handleOpenCreateModal = () => {
    setNewTemplateName('');
    setNewTemplateDesc('');
    setNewTemplateSource(sources[0]?.source || 'applications');
    setIsCreateModalOpen(true);
  };

  const handleConfirmCreate = () => {
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    const sourceToUse = newTemplateSource || sources[0]?.source || 'applications';
    setIsCreateModalOpen(false);
    navigate('/automation/templates/editor', {
      state: {
        name: newTemplateName.trim(),
        description: newTemplateDesc.trim(),
        source: sourceToUse,
      },
    });
  };

  // Duplicate template
  const handleDuplicate = async (template: HtmlTemplateItem) => {
    const loader = toast.loading('Duplicating template...');
    try {
      const clientId =
        currentLevel &&
          currentLevel.name !== 'global' &&
          (currentLevel.id !== undefined || currentLevel.client_id !== undefined)
          ? Number(currentLevel.id !== undefined ? currentLevel.id : currentLevel.client_id)
          : undefined;

      const isGlobal = clientId === undefined;

      const payload = {
        name: `${template.name} (Copy)`,
        description: template.description,
        source: template.source,
        is_active: true,
        is_global: isGlobal,
        client_id: !isGlobal ? clientId : undefined,
        open_template: template.open_template,
        close_template: template.close_template,
      };
      await htmlTemplatesApi.createTemplate(payload);
      toast.success('Template duplicated successfully', { id: loader });
      fetchData();
    } catch (err: any) {
      toast.error('Failed to duplicate template', { id: loader });
    }
  };

  // Delete template
  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    if (templateToDelete.is_global && isClientLevel) {
      toast.error('Global templates cannot be deleted at client level');
      setTemplateToDelete(null);
      return;
    }
    try {
      await htmlTemplatesApi.deleteTemplate(templateToDelete.id);
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
      fetchData();
    } catch (err: any) {
      toast.error('Failed to delete template');
    }
  };

  // Open Preview with latest full template details and reset preview controls
  const handleOpenPreview = async (template: HtmlTemplateItem) => {
    setPreviewTab('open');
    setPreviewDeviceView('desktop');
    setPreviewShowRawHtml(false);
    setPreviewTemplate(template);
    try {
      const res = await htmlTemplatesApi.getTemplateById(template.id);
      const fetched = res?.data?.id ? res.data : (res?.id ? res : null);
      if (fetched) {
        setPreviewTemplate((prev) => {
          if (!prev) return fetched;
          const prevOpenWidgets = prev.open_template?.widgets || [];
          const fetchedOpenWidgets = fetched.open_template?.widgets || [];
          const openToUse =
            fetchedOpenWidgets.length > 0 || prevOpenWidgets.length === 0
              ? fetched.open_template
              : prev.open_template;

          const prevCloseWidgets = prev.close_template?.widgets || [];
          const fetchedCloseWidgets = fetched.close_template?.widgets || [];
          const closeToUse =
            fetchedCloseWidgets.length > 0 || prevCloseWidgets.length === 0
              ? fetched.close_template
              : prev.close_template;

          return {
            ...prev,
            ...fetched,
            open_template: openToUse || prev.open_template,
            close_template: closeToUse || prev.close_template,
          };
        });
      }
    } catch (err) {
      console.error('Failed to fetch full template details for preview', err);
    }
  };

  // Pagination calculation
  const totalItems = templates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTemplates = templates.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="flex flex-col space-y-2.5">
      {/* Top Control Bar matching RulesTab.tsx */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-2.5 bg-white/80 dark:bg-gray-800/40 p-2.5 border border-gray-200 dark:border-gray-700/50 rounded-xl shadow-sm dark:shadow-none backdrop-blur-md">
        <div>
          <div className="flex items-center gap-2">
            <FileCode2 className="w-6 h-6 text-white bg-orange-600 p-1 rounded-lg shrink-0" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap tracking-tight">
              Templates
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end w-full">
          {/* Refresh button */}
          <button
            onClick={() => fetchData()}
            className="p-1.5 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0 cursor-pointer"
            title="Refresh list"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Search bar */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-200" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3.5 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* Create Template button */}
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 dark:bg-orange-700 dark:hover:bg-orange-600 active:scale-95 text-white text-[11px] font-bold rounded-lg transition-all duration-150 shadow-md hover:shadow-orange-500/10 cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            CREATE TEMPLATE
          </button>
        </div>
      </div>

      {/* Table Section - height wraps content with no empty gap */}
      <div className="overflow-x-auto min-w-full relative custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-xl bg-white dark:bg-gray-800/20 shadow-sm dark:shadow-none backdrop-blur-md">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          </div>
        ) : paginatedTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-200 space-y-2">
            <AlertCircle className="w-8 h-8 text-gray-400 dark:text-gray-200" />
            <p className="text-xs">No HTML templates found</p>
          </div>
        ) : (
          <table className="w-full text-[11px] text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/50 backdrop-blur-md text-gray-600 dark:text-gray-100 uppercase tracking-widest text-[10px] font-bold select-none">
                {/* Template Name Header with Sort */}
                <th
                  className="px-3.5 py-2.5 min-w-[200px] cursor-pointer group hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1.5">
                    Template
                    <SortIcon field="name" />
                  </div>
                </th>

                {/* Description Header with Sort */}
                <th
                  className="px-3.5 py-2.5 min-w-[220px] cursor-pointer group hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors select-none"
                  onClick={() => handleSort('description')}
                >
                  <div className="flex items-center gap-1.5">
                    Description
                    <SortIcon field="description" />
                  </div>
                </th>

                {/* Source Header with Sort */}
                <th
                  className="px-3.5 py-2.5 min-w-[140px] cursor-pointer group hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors select-none"
                  onClick={() => handleSort('source')}
                >
                  <div className="flex items-center gap-1.5">

                    Source
                    <SortIcon field="source" />
                  </div>
                </th>

                {/* Created Header with Sort */}
                <th
                  className="px-3.5 py-2.5 min-w-[160px] cursor-pointer group hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors select-none"
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center gap-1.5">
                    Created
                    <SortIcon field="created" />
                  </div>
                </th>

                {/* Updated Header with Sort */}
                <th
                  className="px-3.5 py-2.5 min-w-[160px] cursor-pointer group hover:bg-gray-100/60 dark:hover:bg-gray-800/60 transition-colors select-none"
                  onClick={() => handleSort('updated')}
                >
                  <div className="flex items-center gap-1.5">
                    Updated
                    <SortIcon field="updated" />
                  </div>
                </th>

                {/* Actions Sticky Header */}
                <th className="sticky right-0 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-md px-3.5 py-2.5 text-center w-16 shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.05)] dark:shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)] z-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
              {paginatedTemplates.map((template) => {
                return (
                  <tr
                    key={template.id}
                    className="hover:bg-gray-50/80 dark:hover:bg-gray-700/20 border-b border-gray-100 dark:border-gray-800/50 transition-colors cursor-pointer group relative"
                    onClick={() => {
                      if (template.is_global && isClientLevel) {
                        handleOpenPreview(template);
                      } else {
                        navigate(`/automation/templates/editor/${template.id}`);
                      }
                    }}
                  >
                    {/* Template Name */}
                    <td className="px-3.5 py-2.5 font-medium select-text">
                      <span className="font-bold text-xs sm:text-[13px] text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                        {template.name}
                      </span>
                    </td>

                    {/* Description (Separate column) */}
                    <td
                      className="px-3.5 py-2.5 text-xs text-gray-600 dark:text-gray-300 select-text max-w-xs truncate"
                      title={template.description || ''}
                    >
                      {template.description || <span className="text-gray-400 dark:text-gray-600">—</span>}
                    </td>

                    {/* Source (Exact data from API) */}
                    <td className="px-3.5 py-2.5 font-medium select-text">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/20 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                        {template.source || '—'}
                      </span>
                    </td>

                    {/* Created Date (without relative time) */}
                    <td className="px-3.5 py-2.5 text-xs text-gray-600 dark:text-gray-200 font-medium select-text whitespace-nowrap">
                      {formatDate(template.created)}
                    </td>

                    {/* Updated Date (without relative time) */}
                    <td className="px-3.5 py-2.5 text-xs text-gray-600 dark:text-gray-200 font-medium select-text whitespace-nowrap">
                      {formatDate(template.updated || template.created)}
                    </td>

                    {/* Actions Menu Trigger (matching RulesTab.tsx) */}
                    <td
                      className="sticky right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-3.5 py-2.5 text-center shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.05)] dark:shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)] z-20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative inline-block text-left">
                        <button
                          onClick={(e) => handleActionMenuToggle(e, template.id)}
                          className={`action-menu-trigger rounded-lg border border-transparent hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 p-1.5 flex items-center justify-center transition-all cursor-pointer ${activeActionMenuId === template.id
                              ? 'border-blue-500 text-blue-600 dark:text-blue-400 ring-2 ring-blue-500/20 dark:ring-blue-500/30 bg-blue-50 dark:bg-blue-900/20'
                              : 'text-gray-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          title="Actions"
                        >
                          <CircleEllipsis className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Footer matching RulesTab.tsx */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2.5 bg-white/80 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 rounded-xl text-xs text-gray-600 dark:text-gray-200 shadow-sm dark:shadow-none">
        <div className="flex items-center gap-3">
          <span>
            Showing <span className="font-semibold text-gray-900 dark:text-white">{templates.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to{' '}
            <span className="font-semibold text-gray-900 dark:text-white">{Math.min(currentPage * itemsPerPage, templates.length)}</span> of{' '}
            <span className="font-semibold text-gray-900 dark:text-white">{templates.length}</span> entries
          </span>
          <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
            <span className="text-[11px] text-gray-500 dark:text-gray-200">Per page:</span>
            <ItemsPerPageDropdown
              value={itemsPerPage}
              onChange={(val) => {
                setItemsPerPage(val);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
            title="First Page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
            title="Previous Page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="px-2 py-0.5 text-xs text-gray-600 dark:text-gray-200 font-medium">
            Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of <span className="text-gray-900 dark:text-white font-bold">{totalPages || 1}</span>
          </span>

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
            title="Next Page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
            title="Last Page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Action Menu Portal matching RulesTab.tsx */}
      {activeActionMenuId && (() => {
        const activeTemplate = templates.find((t) => t.id === activeActionMenuId);
        if (!activeTemplate) return null;
        const isGlobalAtClient = Boolean(activeTemplate.is_global && isClientLevel);

        return createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998] cursor-default"
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionMenuId(null);
              }}
            />
            <div
              className="fixed w-44 bg-white dark:bg-[#0d1527] border border-gray-200 dark:border-gray-800/90 rounded-xl shadow-2xl overflow-hidden py-1 z-[9999] text-left action-menu-portal"
              style={{
                top: actionMenuPosition.top,
                left: actionMenuPosition.left,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenuId(null);
                  handleOpenPreview(activeTemplate);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <Eye className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                <span>{isGlobalAtClient ? 'View Template' : 'Preview'}</span>
              </button>

              {!isGlobalAtClient && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveActionMenuId(null);
                    navigate(`/automation/templates/editor/${activeTemplate.id}`);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
                >
                  <Edit className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                  <span>Edit Template</span>
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenuId(null);
                  handleDuplicate(activeTemplate);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <Copy className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                <span>{isGlobalAtClient ? 'Duplicate to Client' : 'Duplicate'}</span>
              </button>

              {!isGlobalAtClient && (
                <>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-800/80" />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      setTemplateToDelete(activeTemplate);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                    <span>Delete Template</span>
                  </button>
                </>
              )}
            </div>
          </>,
          document.body
        );
      })()}

      {/* Delete Confirmation Modal using CustomConfirm */}
      <CustomConfirm
        isOpen={templateToDelete !== null}
        title="Delete HTML Template"
        message={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="red"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setTemplateToDelete(null)}
      />

      {/* Quick Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl w-full max-w-4xl h-[88vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
            {/* Unified Modal Header: All controls in single sleek bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-950/90 flex-none">
              <div className="flex items-center gap-2.5 min-w-0 pr-3">
                <div className="p-1.5 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-400 flex-none">
                  <Eye className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide truncate">
                  {previewTemplate.name}
                </h3>
              </div>

              {/* Controls in Single Header */}
              <div className="flex items-center gap-2 flex-none">
                {/* Alert Type Switcher (Open vs Close) */}
                <div className="flex bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => setPreviewTab('open')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${previewTab === 'open'
                        ? 'bg-orange-600 dark:bg-orange-700 text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Open Alert
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewTab('close')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${previewTab === 'close'
                        ? 'bg-emerald-600 dark:bg-emerald-700 text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Close Alert
                  </button>
                </div>

                <div className="w-px h-4 bg-gray-200 dark:bg-gray-800 mx-0.5" />

                {/* Device View Switcher */}
                <div className="flex bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceView('desktop')}
                    className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${previewDeviceView === 'desktop'
                        ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    title="Desktop width (Full width)"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceView('mobile')}
                    className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${previewDeviceView === 'mobile'
                        ? 'bg-orange-600 dark:bg-orange-500 text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    title="Mobile width (380px)"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Raw HTML Code vs Visual Preview Toggle */}
                <button
                  type="button"
                  onClick={() => setPreviewShowRawHtml(!previewShowRawHtml)}
                  className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer border ${previewShowRawHtml
                      ? 'bg-cyan-50 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-500/30'
                      : 'border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-800'
                    }`}
                  title={previewShowRawHtml ? 'View Visual Preview' : 'View Raw HTML Code'}
                >
                  {previewShowRawHtml ? <Eye className="w-3.5 h-3.5" /> : <Code className="w-3.5 h-3.5" />}
                </button>

                <div className="w-px h-4 bg-gray-200 dark:bg-gray-800 mx-0.5" />

                <button
                  type="button"
                  onClick={() => setPreviewTemplate(null)}
                  className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                  title="Close Preview"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Body: Clean, Borderless Embedded Preview */}
            <div className="flex-1 overflow-hidden">
              <HtmlEmailPreview
                config={
                  previewTab === 'open'
                    ? previewTemplate.open_template || {}
                    : previewTemplate.close_template || {}
                }
                hideHeader
                borderless
                deviceView={previewDeviceView}
                onDeviceViewChange={setPreviewDeviceView}
                showRawHtml={previewShowRawHtml}
                onShowRawHtmlChange={setPreviewShowRawHtml}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal matching RulesTab.tsx style */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmCreate();
            }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 w-[440px] max-w-full shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150"
          >
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3">
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Create HTML Template</h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Set up template name, collection source, and description</p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-white p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                  Template Name <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g. Critical Port Alert Notification"
                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                  Collection Source <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <CustomSelect
                  options={sourceOptions}
                  value={newTemplateSource}
                  onChange={(val: string) => setNewTemplateSource(val)}
                  placeholder="Select Collection Source"
                  className="w-full"
                  searchable
                  searchPlaceholder="Search sources..."
                />
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  The data model providing dynamic variables and table records.
                </p>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                  Description
                </label>
                <textarea
                  value={newTemplateDesc}
                  onChange={(e) => setNewTemplateDesc(e.target.value)}
                  rows={2}
                  placeholder="Optional description of when this notification is sent..."
                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-200 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={!newTemplateName.trim()}
                className="px-3.5 py-1.5 bg-orange-600 hover:bg-orange-500 dark:bg-orange-700 dark:hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-bold rounded-lg transition-all shadow-md hover:shadow-orange-500/10 cursor-pointer"
              >
                CONTINUE
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TemplatesTab;
